import { Box, FormControlLabel, Grid } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { ReactComponent as CrossIcon } from '../../../assets/images/cross-icon.svg';
import './UptimeMonitoringModal';
import { Switch, Typography } from '@mui/material';
import { UrlMaintenanceInterface } from '../../../pages/UptimeMonitoring/URLMonitoring';
import MaintananceDurationSection from './MaintananceDurationSection';
import { useFetchData } from '../../../hooks/useFetchData';
import { config } from '../../../pages/UptimeMonitoring/config';
import Loading from '../../Loading/Loading';
import MaintenanceInfo from './MaintenanceInfo';

type PauseType = 'daily' | 'weekly' | 'monthly' | 'date_range';

interface UrlMaintenanceModal {
  show: boolean;
  onClose?: { (): void };
  urlData?: any;
  isAddMaintenanceUrl: boolean;
  handleUrlMaintenanceSubmition?: (
    props: UrlMaintenanceInterface
  ) => Promise<void>;
  handleUrlMaintenanceScheduleSubmit?: (props: any) => Promise<void>;
}

const UrlMaintenanceModal: React.FC<UrlMaintenanceModal> = (props) => {
  const {
    show,
    urlData,
    onClose,
    handleUrlMaintenanceSubmition,
    isAddMaintenanceUrl,
    handleUrlMaintenanceScheduleSubmit,
  } = props;

  const [maintenanceScheduleData, setMaintenanceScheduleData] = useState<any>(
    {}
  );
  const [isSaving, setIsSaving] = useState(false);
  const [startDate, setStartDate] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [endDate, setEndDate] = useState<any>(null);

  const [endTimeError, setEndTimeError] = React.useState(false);
  const [isMaintenanceEnabled, setIsMaintenanceEnabled] = useState(true);
  const [startTimeError, setStartTimeError] = React.useState(false);
  const [startExceedTimeError, setStartExceedTimeError] = React.useState(false);
  const [pauseType, setPauseType] = useState<PauseType>('daily');
  const [initializedPauseType, setInitializedPauseType] = useState(false);
  const [DailyTimeRange, setDailyTimeRange] = useState('00:00-00:00');
  const [WeeklyTimeRange, setWeeklyTimeRange] = useState('00:00-00:00');
  const [MonthlyTimeRange, setMonthlyTimeRange] = useState('00:00-00:00');
  const [scheduleIds, setScheduleIds] = useState({
    daily: null,
    weekly: null,
    monthly: null,
    date_range: null,
  });
  const [names, setNames] = useState({
    daily: '',
    weekly: '',
    monthly: '',
    date_range: '',
  });

  const [timeRanges, setTimeRanges] = useState({
    daily: '',
    weekly: '',
    monthly: '',
    date_range: '',
  });

  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const [maintenanceMap, setMaintenanceMap] = useState<Record<string, any>>({});

  const [errors, setErrors] = useState({
    daily: '',
    weekly: '',
    monthly: '',
    date_range: '',
  });

  const [dayStates, setDayStates] = useState<
    Record<string, { enabled: boolean; note: string }>
  >(
    Object.fromEntries(
      daysOfWeek.map((day) => [day, { enabled: false, note: '' }])
    )
  );
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState<number | ''>('');

  const handleCheckboxChange = (day: string) => {
    setDayStates((prev) => {
      const current = prev?.[day] ?? { enabled: false, note: '' };
      return {
        ...prev,
        [day]: {
          ...current,
          enabled: !current.enabled,
        },
      };
    });
  };

  const dateChange = (event: any) => {
    if (event.getTime() < new Date().getTime() + 120000) {
      setStartDate(new Date().getTime() + 120000);
      setStartTimeError(true);
    } else {
      setStartTimeError(false);
    }

    if (event.getTime() >= endDate && endDate) {
      setStartExceedTimeError(true);
    }
    setStartExceedTimeError(false);
    setStartDate(event.getTime());

    // Reset end date if it has the same time as start date
    if (endDate && event && event.getTime() === endDate) {
      setEndDate(null);
    }
  };
  const endDateChange = (event: any) => {
    // Validate if end date is greater than start date
    if (event && startDate && event.getTime() <= startDate) {
      setEndTimeError(true);
    } else {
      setEndTimeError(false);
    }
    setEndDate(event.getTime());
  };

  const handleMaintenanceSwitchChange = (event: any) => {
    setIsMaintenanceEnabled(event.target.checked);
  };

  const handlePauseTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (
      value === 'daily' ||
      value === 'weekly' ||
      value === 'monthly' ||
      value === 'date_range'
    ) {
      setPauseType(value);
    }
  };

  const validateDailyTimeRange = () => {
    if (!DailyTimeRange || !DailyTimeRange.includes('-'))
      return 'Please enter a time range.';

    const [startTime, endTime] = DailyTimeRange.split('-');
    if (!startTime || !endTime) return 'Please enter a time range.';

    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    if (toMinutes(startTime) >= toMinutes(endTime)) {
      return 'Start time must be before end time.';
    }

    return '';
  };

  const validateDateRange = () => {
    if (!startDate || !endDate) {
      return 'Please select both start and end date/time.';
    }

    if (startDate >= endDate) {
      return 'Start date/time must be before end date/time.';
    }

    return '';
  };

  const validateWeekly = () => {
    const anyDayEnabled = Object.values(dayStates).some(
      (state) => state.enabled
    );
    if (!anyDayEnabled) return 'Please select at least one day.';

    if (!WeeklyTimeRange || !WeeklyTimeRange.includes('-'))
      return 'Please enter a time range.';

    const [startTime, endTime] = WeeklyTimeRange.split('-');
    if (!startTime || !endTime) return 'Please enter a time range.';

    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    if (toMinutes(startTime) >= toMinutes(endTime)) {
      return 'Start time must be before end time.';
    }

    return '';
  };

  const validateMonthly = () => {
    if (
      !selectedDayOfMonth ||
      selectedDayOfMonth < 1 ||
      selectedDayOfMonth > 31
    ) {
      return 'Please select a valid day of the month.';
    }

    if (!MonthlyTimeRange || !MonthlyTimeRange.includes('-')) {
      return 'Please enter a time range.';
    }

    const [startTime, endTime] = MonthlyTimeRange.split('-');
    if (!startTime || !endTime) {
      return 'Please enter a complete time range.';
    }

    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    if (toMinutes(startTime) >= toMinutes(endTime)) {
      return 'Start time must be before end time.';
    }

    return '';
  };

  const isSaveEnabled = useCallback(() => {
    switch (pauseType) {
      case 'daily':
        return validateDailyTimeRange() === '';

      case 'date_range':
        return validateDateRange() === '';

      case 'weekly':
        return validateWeekly() === '';

      case 'monthly':
        return validateMonthly() === '';

      default:
        return false;
    }
  }, [
    pauseType,
    DailyTimeRange,
    startDate,
    endDate,
    WeeklyTimeRange,
    MonthlyTimeRange,
    dayStates,
    selectedDayOfMonth,
  ]);

  const liveMaintenanceInfo = useMemo(() => {
    const info: Record<string, any> = {};

    if (validateDailyTimeRange() === '') {
      info.daily = {
        Name: names.daily,
        DailyTimeRange: DailyTimeRange,
      };
    }

    if (validateWeekly() === '') {
      info.weekly = {
        Name: names.weekly,
        WeeklyTimeRange: WeeklyTimeRange,
        WeeklyDays: Object.keys(dayStates).filter(
          (day) => dayStates[day].enabled
        ),
      };
    }

    if (validateMonthly() === '') {
      info.monthly = {
        Name: names.monthly,
        MonthlyTimeRange: MonthlyTimeRange,
        MonthlyDay: selectedDayOfMonth,
      };
    }

    if (validateDateRange() === '') {
      info.date_range = {
        Name: names.date_range,
        StartDate: startDate,
        EndDate: endDate,
      };
    }

    return info;
  }, [
    names,
    DailyTimeRange,
    WeeklyTimeRange,
    MonthlyTimeRange,
    dayStates,
    selectedDayOfMonth,
    startDate,
    endDate,
  ]);

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple clicks

    let hasError = false;
    const newErrors: typeof errors = { ...errors };

    const validators = {
      daily: validateDailyTimeRange,
      weekly: validateWeekly,
      monthly: validateMonthly,
      date_range: validateDateRange,
    };

    const error = validators[pauseType]?.();
    if (error) {
      newErrors[pauseType] = error;
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      console.log('Validation errors present');
      return;
    }

    const payload: any[] = [];

    const commonFields = {
      urlId: urlData.Id,
      timezone: 'UTC',
    };

    if (validateDailyTimeRange() === '') {
      payload.push({
        ...(isEditMode && { scheduleId: scheduleIds.daily }),
        ...commonFields,
        name: names.daily,
        pauseType: 'daily',
        dailyTimeRange: DailyTimeRange,
      });
    }

    if (validateWeekly() === '') {
      payload.push({
        ...(isEditMode && { scheduleId: scheduleIds.weekly }),
        ...commonFields,
        name: names.weekly,
        pauseType: 'weekly',
        weeklyDays: Object.entries(dayStates)
          .filter(([_, v]) => v.enabled)
          .map(([day]) => day)
          .join(','),
        weeklyTimeRange: WeeklyTimeRange,
      });
    }

    if (validateMonthly() === '') {
      payload.push({
        ...(isEditMode && { scheduleId: scheduleIds.monthly }),
        ...commonFields,
        name: names.monthly,
        pauseType: 'monthly',
        monthlyDay: selectedDayOfMonth,
        monthlyTimeRange: MonthlyTimeRange,
      });
    }

    if (validateDateRange() === '') {
      payload.push({
        ...(isEditMode && { scheduleId: scheduleIds.date_range }),
        ...commonFields,
        name: names.date_range,
        pauseType: 'date_range',
        startDate,
        endDate,
      });
    }

    const finalSubmission = {
      existingData: maintenanceScheduleData,
      isEditMode: isEditMode,
      isMaintenanceEnabled,
      Id: urlData.Id,
      payload,
    };

    try {
      setIsSaving(true); // 🔒 Disable button
      await handleUrlMaintenanceScheduleSubmit?.(finalSubmission);
    } finally {
      setIsSaving(false); // 🔓 Re-enable button
    }
  };

  const handleNameChange = useCallback(
    (val: string) => {
      setNames((prev) => ({
        ...prev,
        [pauseType]: val,
      }));
    },
    [pauseType]
  );

  const urlMaintenanceCondition = () => {
    if (!isAddMaintenanceUrl) {
      if (!isMaintenanceEnabled) return true;

      if (startTimeError || startExceedTimeError || endTimeError) return false;

      const urlStart = urlData.date_range?.startDate
        ? +new Date(urlData.date_range.startDate)
        : null;
      const urlEnd = urlData.date_range?.endDate
        ? +new Date(urlData.date_range.endDate)
        : null;

      if (urlStart === null || urlEnd === null) return false;

      return startDate !== urlStart || endDate !== urlEnd;
    }

    if (startTimeError || startExceedTimeError || endTimeError) return false;

    return true;
  };

  const shouldFetch = Boolean(urlData?.Id);

  const { data, loader: isSubmitting } = useFetchData({
    fetchData: () => config.getUrlMaintenanceSchedule({ urlId: urlData.Id }),
    fetchOnMount: shouldFetch,
  });

  useEffect(() => {
    if (!isSubmitting && Array.isArray(data) && data.length > 0) {
      if (data) {
        setMaintenanceScheduleData(data);
        setIsEditMode(true);
      }
    }
  }, [data, isSubmitting]);

  useEffect(() => {
    if (
      !initializedPauseType &&
      Array.isArray(maintenanceScheduleData) &&
      maintenanceScheduleData.length > 0 &&
      maintenanceScheduleData[0].PauseType
    ) {
      const priorityOrder: PauseType[] = [
        'daily',
        'weekly',
        'monthly',
        'date_range',
      ];

      const foundType = priorityOrder.find((type) =>
        maintenanceScheduleData.some((item) => item.PauseType === type)
      );

      const selectedPauseType =
        foundType || (maintenanceScheduleData[0].PauseType as PauseType);

      setPauseType(selectedPauseType);
      setInitializedPauseType(true);
    }
  }, [maintenanceScheduleData, initializedPauseType]);

  useEffect(() => {
    if (Array.isArray(maintenanceScheduleData)) {
      const grouped = maintenanceScheduleData.reduce((acc, item) => {
        acc[item.PauseType] = item;
        return acc;
      }, {});
      setMaintenanceMap(grouped);
    }
  }, [maintenanceScheduleData]);

  useEffect(() => {
    if (isEditMode && Object.keys(maintenanceMap).length) {
      setNames({
        daily: maintenanceMap.daily?.Name ?? '',
        weekly: maintenanceMap.weekly?.Name ?? '',
        monthly: maintenanceMap.monthly?.Name ?? '',
        date_range: maintenanceMap.date_range?.Name ?? '',
      });

      setScheduleIds({
        daily: maintenanceMap.daily?.Id ?? '',
        weekly: maintenanceMap.weekly?.Id ?? '',
        monthly: maintenanceMap.monthly?.Id ?? '',
        date_range: maintenanceMap.date_range?.Id ?? '',
      });

      setTimeRanges({
        daily: maintenanceMap.daily?.DailyTimeRange ?? '',
        weekly: maintenanceMap.weekly?.WeeklyTimeRange ?? '',
        monthly: maintenanceMap.monthly?.MonthlyTimeRange ?? '',
        date_range: '',
      });

      if (maintenanceMap.weekly?.WeeklyDays) {
        setDayStates((prev) => {
          const newState = { ...prev };
          daysOfWeek.forEach((day) => {
            newState[day] = {
              enabled: maintenanceMap.weekly.WeeklyDays.includes(day),
              note: '',
            };
          });
          return newState;
        });
      }
      const monthlyDayValue = maintenanceMap.monthly?.MonthlyDay;
      setSelectedDayOfMonth(
        typeof monthlyDayValue === 'number' ? monthlyDayValue : ''
      );

      setDailyTimeRange(maintenanceMap.daily?.DailyTimeRange ?? '00:00-00:00');
      setWeeklyTimeRange(
        maintenanceMap.weekly?.WeeklyTimeRange ?? '00:00-00:00'
      );
      setMonthlyTimeRange(
        maintenanceMap.monthly?.MonthlyTimeRange ?? '00:00-00:00'
      );

      setStartDate(
        maintenanceMap.date_range?.StartDate
          ? new Date(maintenanceMap.date_range.StartDate)
          : null
      );

      setEndDate(
        maintenanceMap.date_range?.EndDate
          ? new Date(maintenanceMap.date_range.EndDate)
          : null
      );
    } else {
      setNames({ daily: '', weekly: '', monthly: '', date_range: '' });
      setTimeRanges({ daily: '', weekly: '', monthly: '', date_range: '' });
      setDayStates(
        Object.fromEntries(
          daysOfWeek.map((day) => [day, { enabled: false, note: '' }])
        )
      );

      setSelectedDayOfMonth('');
      setDailyTimeRange('00:00-00:00');
      setWeeklyTimeRange('00:00-00:00');
      setMonthlyTimeRange('00:00-00:00');
      setStartDate(null);
      setEndDate(null);
    }
  }, [maintenanceMap, isEditMode]);

  return (
    <Modal
      isOpen={show}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      bodyOpenClassName="no-scroll"
      ariaHideApp={false}
      style={{ overlay: { background: 'rgba(0,0,0,.5)', zIndex: 9999 } }}
      className="fixModal"
    >
      <Grid container spacing={0}>
        <Grid item md={12} sm={12} xs={12} className="modalHeader">
          <div>
            {isEditMode === true && (
              <span className="modalTitle">Edit URL Maintenance</span>
            )}
            {isEditMode === false && (
              <span className="modalTitle">Add URL Maintenance</span>
            )}
            <span className="modalClose" onClick={onClose}>
              <CrossIcon width={20} height={20} />
            </span>
          </div>
        </Grid>
        <Box
          padding="12px"
          borderRadius={2}
          style={{ width: '90%', margin: 'auto' }}
          display="flex"
          flexDirection="column"
        >
          {isSubmitting ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <Loading />
            </Box>
          ) : !isEditMode ? (
            <>
              <p className="fw-500 mt-10 font-16px">Maintenance Duration</p>
              <MaintenanceInfo liveData={liveMaintenanceInfo} />
              <MaintananceDurationSection
                pauseType={pauseType}
                handlePauseTypeChange={handlePauseTypeChange}
                names={names}
                handleNameChange={handleNameChange}
                dayStates={dayStates}
                daysOfWeek={daysOfWeek}
                handleCheckboxChange={handleCheckboxChange}
                selectedDayOfMonth={selectedDayOfMonth}
                setSelectedDayOfMonth={setSelectedDayOfMonth}
                startDate={startDate}
                endDate={endDate}
                dateChange={dateChange}
                endDateChange={endDateChange}
                startTimeError={startTimeError}
                startExceedTimeError={startExceedTimeError}
                endTimeError={endTimeError}
                DailyTimeRange={DailyTimeRange}
                WeeklyTimeRange={WeeklyTimeRange}
                MonthlyTimeRange={MonthlyTimeRange}
                setDailyTimeRange={setDailyTimeRange}
                setWeeklyTimeRange={setWeeklyTimeRange}
                setMonthlyTimeRange={setMonthlyTimeRange}
                errors={errors}
              />
            </>
          ) : (
            <div>
              <p className="fw-500 mt-10 mb-5 font-16px">Under Maintenance</p>
              <MaintenanceInfo liveData={liveMaintenanceInfo} />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={2}
              >
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isMaintenanceEnabled}
                        onChange={handleMaintenanceSwitchChange}
                        color="primary"
                      />
                    }
                    label="Enable Maintenance"
                  />
                </Box>
              </Box>
              <MaintananceDurationSection
                pauseType={pauseType}
                handlePauseTypeChange={handlePauseTypeChange}
                names={names}
                handleNameChange={handleNameChange}
                dayStates={dayStates}
                daysOfWeek={daysOfWeek}
                handleCheckboxChange={handleCheckboxChange}
                selectedDayOfMonth={selectedDayOfMonth}
                setSelectedDayOfMonth={setSelectedDayOfMonth}
                startDate={startDate}
                endDate={endDate}
                dateChange={dateChange}
                endDateChange={endDateChange}
                startTimeError={startTimeError}
                startExceedTimeError={startExceedTimeError}
                endTimeError={endTimeError}
                DailyTimeRange={DailyTimeRange}
                WeeklyTimeRange={WeeklyTimeRange}
                MonthlyTimeRange={MonthlyTimeRange}
                setDailyTimeRange={setDailyTimeRange}
                setWeeklyTimeRange={setWeeklyTimeRange}
                setMonthlyTimeRange={setMonthlyTimeRange}
                errors={errors}
              />
            </div>
          )}
        </Box>
        {!isSubmitting && (
          <div className="mt-0 modalFooter">
            {
              <button
                type="button"
                disabled={isSaving || !isSaveEnabled()}
                // disabled={
                //   startDate && endDate && urlMaintenanceCondition() ? false : true
                // }
                className="btn btn-primary ml-10"
                onClick={() => {
                  // handleUrlMaintenanceSubmition?.({
                  //   endDate,
                  //   startDate,
                  //   Id: urlData.Id,
                  //   isEditMode,
                  //   isMaintenanceEnabled,
                  // }),
                  handleSave(), onClose;
                }}
              >
                Save
              </button>
            }
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        )}
      </Grid>
    </Modal>
  );
};

export default UrlMaintenanceModal;
